import type { EntityState, PayloadAction } from '@reduxjs/toolkit';
import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import * as hourStatesApi from 'api/hours/hourStates.api';
import {
  HOUR_STATE_BILLED,
  HOUR_STATE_BILLED_AND_LOCKED,
  HOUR_STATE_INVOICED,
  HOUR_STATE_LOCKED,
} from 'constants/filters';
import { isEqual } from 'lodash-es';
import { startAppListening } from 'redux/middleware/listenerMiddleware';
import type { HourState, HourStatePermissionsPayload } from 'redux/modules/hourStates.schema';
import { getAccountFeature } from 'redux/selectors/accounts';
import { getIsUserAdmin, getUserRoleId } from 'redux/selectors/user';
import type { IState } from 'redux/state';
import { stripHexPrefix } from 'utils/colors';
import debug from 'utils/debug';
import { defaultState, defaultStateId } from './hourStates.constants';
import {
  synthesizeStatePermissionForRole,
  updateHoursWithDeletedState,
  updateHoursWithSavedState,
} from './hourStates.utils';
import { fetchedRoles } from './roles.types';

export interface HourStatesState {
  states: EntityState<HourState, HourState['id']>;
  draft: Partial<HourState> | null;
  hasLoaded: boolean;

  /** Keeps a copy of the admin role ID to simplify update logic. */
  adminRoleId: number;
}

export const hourStatesAdapter = createEntityAdapter<HourState>();

const selectors = hourStatesAdapter.getSelectors((state: IState) => state.hourStates.states);

export const getHourStates = (state: IState) =>
  selectors.selectEntities(state) as Record<number, HourState>;

export const getHourStateById = (state: IState, id: number) => selectors.selectById(state, id);

export const readHourStatesState = (state: IState) => state.hourStates;

export const getHasLoadedHourStates = createSelector(
  [readHourStatesState],
  hourStates => hourStates.hasLoaded
);

export const getNonDeletedHourStates = createSelector(
  [getHourStates],
  (statesById): ReturnType<typeof getHourStates> => {
    const nonDeletedHourStates: Record<number, HourState> = {};
    for (const id in statesById) {
      const state = statesById[id];
      if (!state.deleted) {
        nonDeletedHourStates[id] = state;
      }
    }
    return nonDeletedHourStates;
  }
);

export const getUserFilterableHourStates = createSelector(
  [
    getHourStates,
    (state: IState) => getAccountFeature(state, 'billing'),
    (state: IState) => getAccountFeature(state, 'invoices'),
    (state: IState) => getAccountFeature(state, 'hour_states'),
  ],
  (
    statesById: ReturnType<typeof getHourStates>,
    hasBillingFeature,
    hasInvoicesFeature,
    hasHourStatesFeature
  ) => {
    const hourStates: Record<number, HourState> = {};
    for (const id in statesById) {
      const state = statesById[id];

      // Ignore invalid and deleted states
      if (!state.name || state.deleted) continue;

      // Ignore billed states if the user doesn't have billing feature
      if (
        !hasBillingFeature &&
        [HOUR_STATE_BILLED, HOUR_STATE_BILLED_AND_LOCKED].includes(state.name)
      )
        continue;

      // Ignore hour states if the user doesn't have hour_states feature
      if (
        !hasHourStatesFeature &&
        [HOUR_STATE_LOCKED, HOUR_STATE_BILLED_AND_LOCKED].includes(state.name)
      )
        continue;

      // Ignore invoiced state if the user doesn't have invoicing feature
      if (!hasInvoicesFeature && HOUR_STATE_INVOICED === state.name) continue;

      hourStates[id] = state;
    }

    return hourStates;
  }
);

export const getUserSelectableHourStates = createSelector(
  [
    getUserFilterableHourStates,
    getUserRoleId,
    (state: IState) => getAccountFeature(state, 'hour_states'),
    (state: IState) => getAccountFeature(state, 'billing'),
  ],
  (
    statesById,
    userRoleId,
    isHourStatesFeatureEnabled,
    isBillingFeatureEnabled
  ): ReturnType<typeof getHourStates> => {
    const userSelectableHourStates: Record<number, HourState> = {};
    for (const id in statesById) {
      const state = statesById[id];

      // Ignore states that the user doesn't have permission for
      if (!state.permissions.some(p => p.role_id === userRoleId)) continue;

      // Ignore system managed states
      if (state.system_managed && state.id !== defaultStateId) continue;

      userSelectableHourStates[id] = state;
    }

    if (isHourStatesFeatureEnabled) return userSelectableHourStates;
    else if (!isHourStatesFeatureEnabled && isBillingFeatureEnabled) {
      const statesToPreserve = [HOUR_STATE_BILLED];
      for (const id in userSelectableHourStates) {
        const state = userSelectableHourStates[id];
        if (!statesToPreserve.includes(state.name)) {
          delete userSelectableHourStates[id];
        }
      }
      return userSelectableHourStates;
    } else {
      return {};
    }
  }
);

export const getShowUpgradeModalForHourStateDropdown = createSelector(
  [
    (state: IState) => getAccountFeature(state, 'hour_states'),
    (state: IState) => getAccountFeature(state, 'billing'),
    getIsUserAdmin,
  ],
  (hasHourStatesFeature, hasBillingFeature, isAdmin): boolean =>
    !hasHourStatesFeature && !hasBillingFeature && isAdmin
);

export const getShowUpgradeModalForHourStateDropdownFilter = createSelector(
  [
    (state: IState) => getAccountFeature(state, 'hour_states'),
    (state: IState) => getAccountFeature(state, 'billing'),
    (state: IState) => getAccountFeature(state, 'invoices'),
    getIsUserAdmin,
  ],
  (hasHourStatesFeature, hasBillingFeature, hasInvoices, isAdmin): boolean =>
    !hasInvoices && !hasHourStatesFeature && !hasBillingFeature && isAdmin
);

export const getShowHourStateDropdown = createSelector(
  [getUserSelectableHourStates],
  (userSelectableHourStates): boolean => {
    return Object.values(userSelectableHourStates).filter(s => s.id !== 0).length > 0;
  }
);

export const getShowHourStateDropdownFilter = createSelector(
  [getUserFilterableHourStates],
  (userFilterableHourStates): boolean => {
    return Object.values(userFilterableHourStates).filter(s => s.id !== 0).length > 0;
  }
);

const initialState: HourStatesState = {
  states: hourStatesAdapter.getInitialState<HourStatesState['states']>({
    ids: [defaultState.id],
    entities: {
      [defaultState.id]: defaultState,
    },
  }),
  draft: null,
  hasLoaded: false,
  adminRoleId: -1,
};

const hourStatesSlice = createSlice({
  name: 'hourStates',
  initialState,
  reducers: {
    upsertStates: (state, action: PayloadAction<Array<Partial<HourState>>>) => {
      const states =
        // Don't insert admin role if we don't have it yet
        state.adminRoleId > 0
          ? action.payload.map(s => {
              // If the update doesn't touch permissions or already has the admin role, just return it
              if (!s.permissions || s.permissions.some(p => p.role_id === state.adminRoleId)) {
                return s;
              }

              // Ensure permission for admin role
              return {
                ...s,
                permissions: s.permissions.concat(
                  synthesizeStatePermissionForRole(state.adminRoleId, s.id)
                ),
              };
            })
          : action.payload;

      state.states = hourStatesAdapter.upsertMany(state.states, states as Array<HourState>);
    },
    finishLoading: state => {
      state.hasLoaded = true;
    },
    setAdminRoleId: (state, action: PayloadAction<number>) => {
      state.adminRoleId = action.payload;
    },
  },
});

export const fetchHourStates = createAsyncThunk('hourStates/fetch', async (_, thunkAPI) => {
  const states = await hourStatesApi.getHourStates();
  thunkAPI.dispatch(hourStatesSlice.actions.upsertStates(states));
  thunkAPI.dispatch(hourStatesSlice.actions.finishLoading());
});

export const saveHourState = createAsyncThunk(
  'hourStates/save',
  async (state: Partial<HourState>, thunkAPI) => {
    const { permissions, ...statePayload } = state;

    statePayload.color &&= stripHexPrefix(statePayload.color).toLowerCase();

    const savedState = statePayload.id
      ? await hourStatesApi.updateHourState(statePayload, statePayload.id)
      : await hourStatesApi.createHourState(statePayload);

    const permissionsDidChange =
      !statePayload.id ||
      !isEqual(
        permissions,
        selectors.selectById(thunkAPI.getState() as IState, statePayload.id)?.permissions
      );

    // If permissions have changed, we need to fire a separate request and wait for its result
    // before updating our local state
    if (permissions && permissionsDidChange) {
      const permissionsPayload: HourStatePermissionsPayload = {
        state_permission: {
          role_ids: [],
          project_lead: false,
          team_lead: false,
        },
      };

      for (const permission of permissions) {
        if (permission.role_id > 0) {
          permissionsPayload.state_permission.role_ids.push(permission.role_id);
        } else if (permission.project_lead) {
          permissionsPayload.state_permission.project_lead = true;
        } else if (permission.team_lead) {
          permissionsPayload.state_permission.team_lead = true;
        }
      }

      savedState.permissions = await hourStatesApi.upsertHourStatePermissions(
        savedState.id,
        permissionsPayload
      );
    }

    thunkAPI.dispatch(hourStatesSlice.actions.upsertStates([savedState]));

    updateHoursWithSavedState(savedState, thunkAPI.dispatch, thunkAPI.getState() as IState);
  }
);

export const deleteHourState = createAsyncThunk(
  'hourStates/delete',
  async (id: number, thunkAPI) => {
    const state = selectors.selectById(thunkAPI.getState() as IState, id);

    if (!state) return;

    await hourStatesApi.deleteHourState(id);

    thunkAPI.dispatch(hourStatesSlice.actions.upsertStates([{ id, deleted: true }]));

    updateHoursWithDeletedState(id, thunkAPI.dispatch, thunkAPI.getState() as IState);
  }
);

export const { actions } = hourStatesSlice;

export default hourStatesSlice.reducer;

// Create role permissions for the default state when roles have been fetched
startAppListening({
  actionCreator: fetchedRoles,
  effect: (action, { dispatch, getState }) => {
    const roles = action.payload;
    const permissions = roles.map(role =>
      synthesizeStatePermissionForRole(role.id, defaultStateId)
    );

    // Update permissions for the default state
    const statesToUpdate: Array<Partial<HourState>> = [{ id: defaultStateId, permissions }];

    // Find the admin role and ensure all states include it in their permissions
    const adminRole = roles.find(role => role.name === 'admin');
    if (adminRole) {
      dispatch(hourStatesSlice.actions.setAdminRoleId(adminRole.id));

      const hourStates = selectors.selectAll(getState());
      for (const hourState of hourStates) {
        // The default state is already handled above
        if (hourState.id === defaultStateId) continue;

        // Don't update states that already have admin permission
        if (hourState.permissions.some(p => p.role_id === adminRole.id)) continue;

        statesToUpdate.push({
          id: hourState.id,
          permissions: hourState.permissions.concat(
            synthesizeStatePermissionForRole(adminRole.id, hourState.id)
          ),
        });
      }
    } else {
      debug.warn('No admin role found');
    }

    dispatch(hourStatesSlice.actions.upsertStates(statesToUpdate));
  },
});
